import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import TypographyInline from "../../../../components/Typography/TypographyInline";
import InputField from "../../../../components/InputFields/InputField";
import { Button } from "../../../../components/Buttons";
import {
  GREY,
  BORDER,
  BASE,
  SUCCESS,
  WARNING,
  ERROR,
  ACTIONS,
} from "../../../../utils/constants/colors";
import { fontWeight } from "../../../../utils/constants/UI";
import { styles } from "./styles";
import { getRequest, postRequest } from "../../../../Apis";
import { taskEndpoints } from "../../../../utils/constants/httpConstants";
import { useNavigate } from "react-router-dom";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR, EMITTER_SUCCESS } from "../../../../utils/constants";
import DashboardLoader from "../../../../components/Loaders/DashboardLoader";

/**
 * Settings → Calling Integration.
 *
 * Two independent things live here, and conflating them is what produced the
 * "a call I placed showed somebody else's identity" report:
 *
 *   1. RING AT: the phone WE RING when you click Call. Any number, your own mobile is fine
 *                 (an Indian mobile calling a US prospect is normal). Nothing is purchased for
 *                 it. With none saved the CRM refuses to place calls rather than ringing
 *                 whatever number happens to sit in the server env.
 *   2. OUTBOUND: what the PROSPECT sees. ONLY a real number on the connected RingCentral
 *                 extension may be used: the list comes FROM RingCentral, is never free text,
 *                 and there is deliberately NO "use my RingCentral default" escape hatch, so
 *                 the identity a prospect sees is always one this rep explicitly owns. When a
 *                 rep has numbers but none chosen, the first is selected for them.
 *
 * Connecting your own RingCentral is what makes the outbound list YOURS. Until then calls go
 * through the shared company extension, which can only ever show the company's own numbers.
 */

const card = {
  border: `1px solid ${BORDER.DEFAULT}`,
  borderRadius: "12px",
  background: BASE.WHITE,
  padding: "20px",
};

/** Banner tones for what came back from the RingCentral round trip. */
const BANNER = {
  success: { bg: SUCCESS.W_50, border: SUCCESS.W_100, dot: SUCCESS.W_500, ink: SUCCESS.W_700 },
  warning: { bg: WARNING.W_50, border: WARNING.W_100, dot: WARNING.W_500, ink: WARNING.W_700 },
  error: { bg: ERROR.W_50, border: ERROR.W_100, dot: ERROR.W_500, ink: ERROR.W_700 },
};

const Label = ({ text, size = "sm", weight = fontWeight.SEMIBOLD, color = GREY.SECONDARY }) => (
  <TypographyInline label={text} size={size} fontWeight={weight} color={color} />
);

const Hint = ({ text }) => (
  <TypographyInline label={text} size="xs" fontWeight={fontWeight.REGULAR} color={GREY.TERTIARY} />
);

/** One selectable outbound number. "Default" marks the active choice. */
const NumberRow = ({ selected, title, subtitle, onSelect }) => (
  <Box
    onClick={onSelect}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      padding: "10px 12px",
      borderRadius: "8px",
      cursor: "pointer",
      border: `1px solid ${selected ? SUCCESS.W_100 : BORDER.DEFAULT}`,
      background: selected ? SUCCESS.W_50 : BASE.WHITE,
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <TypographyInline
        label={title}
        size="sm"
        fontWeight={fontWeight.SEMIBOLD}
        color={GREY.PRIMARY}
        isEllipses
      />
      <Box sx={{ marginTop: "2px" }}>
        <Hint text={subtitle || ""} />
      </Box>
    </Box>
    <span
      style={{
        flex: "0 0 auto",
        fontSize: "11.5px",
        fontWeight: 600,
        color: selected ? SUCCESS.W_700 : GREY.TERTIARY,
      }}
    >
      {selected ? "Default" : "Set default"}
    </span>
  </Box>
);

const CallingSetting = () => {
  const classes = styles();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState(null);

  const [state, setState] = useState({
    oauth_enabled: false,
    connected: false,
    needs_reauth: false,
    account_email: null,
    extension_number: null,
    rc_user_name: null,
    available_numbers: [],
  });
  const [ringAt, setRingAt] = useState("");
  const [savedRingAt, setSavedRingAt] = useState("");
  const [callerId, setCallerId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const res = await getRequest(navigate, taskEndpoints.voiceStatus);
      const d = res?.data?.data || {};
      setState({
        oauth_enabled: !!d.oauth_enabled,
        connected: !!d.connected,
        needs_reauth: !!d.needs_reauth,
        account_email: d.account_email || null,
        extension_number: d.extension_number || null,
        rc_user_name: d.rc_user_name || null,
        available_numbers: Array.isArray(d.available_numbers) ? d.available_numbers : [],
      });
      setRingAt(d.ring_at_number || "");
      setSavedRingAt(d.ring_at_number || "");
      // Only a real account number may be shown to a prospect. There is deliberately no
      // "let RingCentral decide" option. If this rep has numbers but none chosen yet, pick
      // the first so the outbound identity is never ambiguous.
      const nums = Array.isArray(d.available_numbers) ? d.available_numbers : [];
      const chosen = d.caller_id_number || "";
      if (chosen) {
        setCallerId(chosen);
      } else if (nums.length) {
        setCallerId(nums[0].phoneNumber);
        postRequest(navigate, taskEndpoints.voiceSettings, {
          ring_at_number: d.ring_at_number || "",
          caller_id_number: nums[0].phoneNumber,
        }).catch(() => {});
      } else {
        setCallerId("");
      }
    } catch (e) {
      // Fall back to the older settings endpoint so the screen still works on an environment
      // where /voice/status has not been deployed yet.
      try {
        const res = await getRequest(navigate, taskEndpoints.voiceSettings);
        const d = res?.data?.data || {};
        setRingAt(d?.settings?.ring_at_number || "");
        setSavedRingAt(d?.settings?.ring_at_number || "");
        setCallerId(d?.settings?.caller_id_number || "");
        setState((s) => ({ ...s, available_numbers: d?.available_numbers || [] }));
      } catch (e2) {
        EventEmitter.emit(EMITTER_ERROR, "Could not load your calling settings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * RingCentral sends the rep back here through the API, which appends ?rc=connected or
   * ?rc=error&rc_msg=... Nothing read those params, so a refused connect looked like a connect
   * that simply did nothing, and the reason ("that extension belongs to somebody else") was
   * thrown away. The params are stripped after reading so a later refresh cannot replay a
   * stale result.
   */
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const outcome = q.get("rc");
    if (!outcome) return;
    const detail = q.get("rc_msg");
    setOutcome(
      outcome === "connected"
        ? detail
          // Connected, but something was changed on the rep's behalf. A green tick over
          // "we cleared your saved number" would be read as "nothing to see here".
          ? { tone: "warning", text: detail }
          : { tone: "success", text: "RingCentral connected. Pick the number prospects see below." }
        : { tone: "error", text: detail || "RingCentral could not be connected. Try again." },
    );
    q.delete("rc");
    q.delete("rc_msg");
    const rest = q.toString();
    window.history.replaceState({}, "", window.location.pathname + (rest ? `?${rest}` : ""));
  }, []);

  const save = async (patch = {}) => {
    const next = {
      ring_at_number: ringAt,
      caller_id_number: callerId,
      ...patch,
    };
    const digits = String(next.ring_at_number || "").replace(/\D/g, "");
    if (next.ring_at_number && digits.length < 10) {
      setError("Enter your phone number with the country code, for example +1 415 555 0134");
      return;
    }
    setError("");
    setSaving(true);
    try {
      await postRequest(navigate, taskEndpoints.voiceSettings, next);
      setSavedRingAt(next.ring_at_number || "");
      EventEmitter.emit(EMITTER_SUCCESS, "Calling settings saved");
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, e?.data?.message || "Could not save calling settings");
    } finally {
      setSaving(false);
    }
  };

  const connect = async () => {
    setBusy("connect");
    try {
      const res = await postRequest(navigate, taskEndpoints.voiceConnect, {
        return_path: window.location.pathname + window.location.search,
      });
      const url = res?.data?.data?.authorize_url;
      if (!url) throw new Error("no authorize url");
      // Full redirect, like the mailbox connect flows. RingCentral will not render its
      // consent screen inside an iframe.
      window.location.href = url;
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, e?.data?.message || "Could not start the RingCentral connection");
      setBusy("");
    }
  };

  const disconnect = async () => {
    setBusy("disconnect");
    try {
      await postRequest(navigate, taskEndpoints.voiceDisconnect, {});
      EventEmitter.emit(EMITTER_SUCCESS, "RingCentral disconnected");
      await load();
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, e?.data?.message || "Could not disconnect");
    } finally {
      setBusy("");
    }
  };

  const refreshNumbers = async () => {
    setBusy("numbers");
    try {
      const res = await getRequest(navigate, taskEndpoints.voiceNumbers);
      const list = res?.data?.data?.available_numbers || [];
      setState((s) => ({ ...s, available_numbers: list }));
      EventEmitter.emit(
        EMITTER_SUCCESS,
        `${list.length} number${list.length === 1 ? "" : "s"} available`,
      );
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, e?.data?.message || "Could not refresh your numbers");
    } finally {
      setBusy("");
    }
  };

  if (loading) {
    return (
      <Box className={classes.container}>
        <Box className={classes.scrollContainer}>
          <Box className={classes.apps}>
            <DashboardLoader message="" />
          </Box>
        </Box>
      </Box>
    );
  }

  const connected = state.connected;
  // A colleague's direct number pasted here rings THEIR phone: RingCentral simply dials the
  // ring-at leg and never checks whose number it is. This is exactly how a call placed by one
  // user ended up ringing another rep's desk, so say so on the spot.
  const digitsOf = (v) => String(v || "").replace(/\D/g, "");
  const ringAtIsAccountNumber =
    !!ringAt &&
    (state.available_numbers || []).some(
      (n) => digitsOf(n.phoneNumber) === digitsOf(ringAt),
    );
  // Name first. A rep checking whether they connected as themselves recognises their name
  // faster than an address, and the whole point of CALL-01 is spotting a connection that
  // silently landed on somebody else's extension.
  const identityLine =
    [
      state.rc_user_name,
      state.account_email,
      state.extension_number ? `Ext ${state.extension_number}` : null,
    ]
      .filter(Boolean)
      .join(" · ") || "RingCentral connected";

  // Which of the four situations a rep is actually in. Everything below reads from this
  // rather than re-deriving the same booleans in five places.
  const phase = !state.oauth_enabled
    ? "off"
    : !connected
      ? "unconnected"
      : (state.available_numbers || []).length === 0
        ? "no_number"
        : "ready";

  const STATUS = {
    off: {
      tone: { bg: ERROR.W_50, border: ERROR.W_100, ink: ERROR.W_700, dot: ERROR.W_500 },
      title: "Switched off for this workspace",
      detail:
        "Your admin has not enabled calling yet, so the Call button will not place a call. Nothing set here takes effect until they do.",
    },
    unconnected: {
      tone: { bg: WARNING.W_50, border: WARNING.W_100, ink: WARNING.W_700, dot: WARNING.W_500 },
      title: state.needs_reauth ? "RingCentral session expired" : "Not connected",
      detail: state.needs_reauth
        ? "Reconnect RingCentral to keep placing calls. Nothing else you set here is lost."
        : "Connect your RingCentral account so calls go out as you, from a number you own.",
    },
    no_number: {
      tone: { bg: WARNING.W_50, border: WARNING.W_100, ink: WARNING.W_700, dot: WARNING.W_500 },
      title: "Connected, no number yet",
      detail: "Your RingCentral extension has no number on it, so a call cannot go out yet.",
    },
    ready: {
      tone: { bg: SUCCESS.W_50, border: SUCCESS.W_100, ink: SUCCESS.W_700, dot: SUCCESS.W_500 },
      title: "Ready to call",
      detail: `Connected as ${identityLine}${callerId ? `. Prospects will see ${callerId}` : ""}`,
    },
  }[phase];

  const stepTone = (kind) =>
    kind === "done"
      ? { border: SUCCESS.W_100, background: SUCCESS.W_50 }
      : kind === "off"
        ? { border: BORDER.LIGHT, background: BASE.BACKGROUND_LIGHT }
        : { border: BORDER.DEFAULT, background: BASE.WHITE };

  const Step = ({ kind, index, title, chip, hint, children }) => (
    <Box
      sx={{
        display: "flex",
        gap: "12px",
        padding: "16px",
        borderRadius: "8px",
        border: `1px solid ${stepTone(kind).border}`,
        background: stepTone(kind).background,
      }}
    >
      <Box
        sx={{
          width: "20px",
          height: "20px",
          borderRadius: "50%",
          flex: "0 0 auto",
          display: "grid",
          placeItems: "center",
          fontSize: "12px",
          lineHeight: "18px",
          fontWeight: 600,
          background: kind === "done" ? SUCCESS.W_500 : kind === "now" ? GREY.PRIMARY : ACTIONS.DEFAULT,
          color: kind === "done" || kind === "now" ? BASE.WHITE : GREY.SECONDARY,
        }}
      >
        {kind === "done" ? "✓" : index}
      </Box>
      <Box sx={{ flex: "1 1 auto", minWidth: 0, opacity: kind === "off" ? 0.6 : 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
          <Label text={title} color={GREY.PRIMARY} />
          {chip}
        </Box>
        {hint ? (
          <Box sx={{ marginTop: "4px" }}>
            <Hint text={hint} />
          </Box>
        ) : null}
        {children}
      </Box>
    </Box>
  );

  const Chip = ({ text, tone }) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: "20px",
        padding: "0 8px",
        borderRadius: "999px",
        fontSize: "12px",
        lineHeight: "18px",
        fontWeight: 600,
        whiteSpace: "nowrap",
        background: tone.bg,
        color: tone.ink,
        border: `1px solid ${tone.border}`,
      }}
    >
      {text}
    </span>
  );

  return (
    // Same shell as the sibling settings tabs: `apps` supplies the white panel and its own
    // scroll, so this tab can never be clipped by the settings frame around it.
    <Box className={classes.container}>
      <Box className={classes.scrollContainer}>
        <Box className={classes.apps}>
          {/* `apps` is an edge-to-edge white panel with no padding of its own, so the gutter has
              to come from here. 16px on the sides matches `headerWrap`, which is what the other
              settings tabs use. */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "16px",
              padding: "16px 16px 24px 16px",
            }}
          >

            {/* What just happened, when the rep has come back from RingCentral. Sits above the
                status band because a refusal explains the status the rep is about to read. */}
            {outcome ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "12px 14px",
                  borderRadius: "8px",
                  background: BANNER[outcome.tone].bg,
                  border: `1px solid ${BANNER[outcome.tone].border}`,
                }}
              >
                <span
                  style={{
                    flex: "0 0 auto",
                    marginTop: "5px",
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: BANNER[outcome.tone].dot,
                  }}
                />
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <TypographyInline
                    size={"sm"}
                    color={BANNER[outcome.tone].ink}
                    fontWeight={fontWeight.MEDIUM}
                    label={outcome.text}
                  />
                </Box>
                <Box sx={{ flex: "0 0 auto" }}>
                  <Button label="Dismiss" variant="ghost" size="sm36" onClick={() => setOutcome(null)} />
                </Box>
              </Box>
            ) : null}

            {/* Where you stand. A rep opens this tab with one question, so answer it first
                instead of making them infer it from four equal boxes. */}
            <Box sx={card}>
              <Box sx={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <TypographyInline
                    size={"md"}
                    color={GREY.PRIMARY}
                    fontWeight={fontWeight.SEMIBOLD}
                    label="Calling Integration"
                  />
                  <Box sx={{ marginTop: "4px" }}>
                    <Hint text="Place calls from a contact record, log the outcome, and keep the recording with the contact." />
                  </Box>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "7px",
                      marginTop: "8px",
                      padding: "5px 10px",
                      borderRadius: "6px",
                      background: STATUS.tone.bg,
                      border: `1px solid ${STATUS.tone.border}`,
                    }}
                  >
                    <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: STATUS.tone.dot }} />
                    <TypographyInline
                      size={"xs"}
                      color={STATUS.tone.ink}
                      fontWeight={fontWeight.SEMIBOLD}
                      label={STATUS.title}
                    />
                  </Box>
                  <Box sx={{ marginTop: "8px", maxWidth: "58ch" }}>
                    <TypographyInline
                      size={"xs"}
                      color={GREY.SECONDARY}
                      fontWeight={fontWeight.REGULAR}
                      label={STATUS.detail}
                    />
                  </Box>
                </Box>
                {state.oauth_enabled ? (
                  <Box sx={{ flex: "0 0 auto", minWidth: "150px" }}>
                    {connected ? (
                      <Button
                        label="Disconnect"
                        variant="ghost"
                        size="sm36"
                        isLoading={busy === "disconnect"}
                        onClick={disconnect}
                      />
                    ) : (
                      <Button
                        label={state.needs_reauth ? "Reconnect RingCentral" : "Connect RingCentral"}
                        variant="primary"
                        size="sm36"
                        isLoading={busy === "connect"}
                        onClick={connect}
                      />
                    )}
                  </Box>
                ) : null}
              </Box>
            </Box>

            {/* Setup. Numbered because the steps genuinely depend on each other: the outbound
                list comes FROM RingCentral, so it cannot exist before the connection does. */}
            <Box sx={card}>
              <Label text="Set up calling" />
              <Box sx={{ marginTop: "4px" }}>
                <Hint text="Three steps, in order. You cannot pick the number prospects see until RingCentral is connected." />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "16px" }}>

                <Step
                  index="1"
                  kind={phase === "off" ? "off" : connected ? "done" : "now"}
                  title="Connect your RingCentral account"
                  chip={
                    connected ? (
                      <Chip
                        text={identityLine}
                        tone={{ bg: SUCCESS.W_50, border: SUCCESS.W_100, ink: SUCCESS.W_700 }}
                      />
                    ) : null
                  }
                  hint={
                    phase === "off"
                      ? "Available once your admin switches calling on."
                      : connected
                        ? "Connected. Disconnect above if you want to use a different RingCentral account."
                        : "RingCentral opens in a new tab and you sign in there, so we never see your password."
                  }
                >
                  {state.oauth_enabled && !connected ? (
                    <Box sx={{ display: "flex", marginTop: "10px" }}>
                      <Box sx={{ flex: "0 0 auto" }}>
                        <Button
                          label={state.needs_reauth ? "Reconnect RingCentral" : "Connect RingCentral"}
                          variant="primary"
                          size="sm36"
                          isLoading={busy === "connect"}
                          onClick={connect}
                        />
                      </Box>
                    </Box>
                  ) : null}
                </Step>

                <Step
                  index="2"
                  kind={!connected ? "off" : phase === "no_number" ? "now" : "done"}
                  title="Number prospects see"
                  hint={
                    !connected
                      ? "Your numbers load from RingCentral once you connect."
                      : "Only a number on your own RingCentral extension can be shown. RingCentral rejects anything else and the call fails."
                  }
                >
                  {connected ? (
                    <>
                      {(state.available_numbers || []).length ? (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" }}>
                          {(state.available_numbers || []).map((n) => (
                            <NumberRow
                              key={n.phoneNumber}
                              selected={callerId === n.phoneNumber}
                              title={n.phoneNumber}
                              subtitle={n.label || n.usageType}
                              onSelect={() => {
                                setCallerId(n.phoneNumber);
                                save({ caller_id_number: n.phoneNumber });
                              }}
                            />
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            marginTop: "10px",
                            padding: "10px",
                            borderRadius: "8px",
                            border: `1px dashed ${BORDER.DEFAULT}`,
                            background: BASE.BACKGROUND_LIGHT,
                          }}
                        >
                          <TypographyInline
                            size={"xs"}
                            color={GREY.SECONDARY}
                            fontWeight={fontWeight.REGULAR}
                            label="Your RingCentral extension has no number on it. Ask your RingCentral admin for a direct number, because a call cannot go out until the prospect has something to see."
                          />
                        </Box>
                      )}
                      <Box sx={{ display: "flex", marginTop: "10px" }}>
                        <Box sx={{ flex: "0 0 auto" }}>
                          <Button
                            label="Refresh from RingCentral"
                            variant="ghost"
                            size="sm36"
                            isLoading={busy === "numbers"}
                            onClick={refreshNumbers}
                          />
                        </Box>
                      </Box>
                    </>
                  ) : null}
                </Step>

                <Step
                  index="3"
                  kind={phase === "off" ? "off" : savedRingAt ? "done" : connected ? "now" : "off"}
                  title="Phone we ring first"
                  hint="We ring this phone when you click Call, then connect you to the prospect. Any phone works, your personal mobile is fine, and it does not need to be a RingCentral number. Include the country code."
                >
                  <Box sx={{ display: "flex", gap: "8px", marginTop: "10px", alignItems: "flex-start", flexWrap: "wrap" }}>
                    <Box sx={{ flex: "1 1 240px", minWidth: 0 }}>
                      <InputField
                        size="sm36"
                        value={ringAt}
                        placeholder="e.g. +1 415 555 0134"
                        onChange={(e) => setRingAt(e?.target?.value)}
                        variant={error ? "error" : "default"}
                        errormessage={error}
                      />
                    </Box>
                    <Box sx={{ flex: "0 0 auto" }}>
                      <Button
                        label="Save"
                        variant="primary"
                        size="sm36"
                        isLoading={saving}
                        disabled={ringAt === savedRingAt}
                        onClick={() => save()}
                      />
                    </Box>
                    {/* Emptying the box and pressing Save already clears it, but nobody guesses
                        that a blank field is a saveable value. A number left over from somebody
                        else's test then reads as a choice this rep made. */}
                    {savedRingAt ? (
                      <Box sx={{ flex: "0 0 auto" }}>
                        <Button
                          label="Remove"
                          variant="ghost"
                          size="sm36"
                          disabled={saving}
                          onClick={() => {
                            setRingAt("");
                            setError("");
                            save({ ring_at_number: "" });
                          }}
                        />
                      </Box>
                    ) : null}
                  </Box>

                  {/* A colleague's direct number pasted here rings THEIR phone: RingCentral
                      dials this leg without checking whose number it is. That is how a call
                      placed by one rep ended up ringing another rep's desk. */}
                  {ringAtIsAccountNumber ? (
                    <Box
                      sx={{
                        marginTop: "8px",
                        padding: "8px 10px",
                        borderRadius: "6px",
                        background: WARNING.W_50,
                        border: `1px solid ${WARNING.W_100}`,
                      }}
                    >
                      <TypographyInline
                        size={"xs"}
                        color={WARNING.W_700}
                        fontWeight={fontWeight.REGULAR}
                        label="This is one of your RingCentral numbers, not a phone you answer. If it rings a desk you are not sitting at, put your own mobile here instead."
                      />
                    </Box>
                  ) : null}

                  <Box
                    component="details"
                    sx={{
                      marginTop: "10px",
                      border: `1px solid ${BORDER.DEFAULT}`,
                      borderRadius: "8px",
                      overflow: "hidden",
                      background: BASE.WHITE,
                    }}
                  >
                    <Box
                      component="summary"
                      sx={{
                        padding: "9px 12px",
                        cursor: "pointer",
                        fontSize: "12px",
                        lineHeight: "18px",
                        fontWeight: 600,
                        color: GREY.SECONDARY,
                        background: BASE.BACKGROUND_LIGHT,
                      }}
                    >
                      How a call actually works
                    </Box>
                    <Box sx={{ display: "flex", gap: "16px", padding: "16px", flexWrap: "wrap" }}>
                      <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        <Hint text="Step 1, we ring you" />
                        <Box sx={{ marginTop: "2px" }}>
                          <Label text="Phone we ring first" color={GREY.PRIMARY} />
                        </Box>
                        <Box sx={{ marginTop: "4px" }}>
                          <TypographyInline
                            size={"xs"}
                            color={GREY.SECONDARY}
                            fontWeight={fontWeight.REGULAR}
                            label="Your desk phone or personal mobile. You pick up, then we dial the prospect."
                          />
                        </Box>
                      </Box>
                      <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                        <Hint text="Step 2, they see this" />
                        <Box sx={{ marginTop: "2px" }}>
                          <Label text="Number prospects see" color={GREY.PRIMARY} />
                        </Box>
                        <Box sx={{ marginTop: "4px" }}>
                          <TypographyInline
                            size={"xs"}
                            color={GREY.SECONDARY}
                            fontWeight={fontWeight.REGULAR}
                            label="The caller ID on the prospect's phone. Must belong to your connected extension."
                          />
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Step>

              </Box>
            </Box>

            {/* Recording is a RingCentral account setting. saveSettings persists the two
                numbers only and nothing enforces a policy at call time, so this is reported,
                never offered as a control. */}
            <Box sx={card}>
              <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
                  <Label text="Call recording" />
                  <Box sx={{ marginTop: "4px", maxWidth: "56ch" }}>
                    <Hint text="Follows your RingCentral account settings. Change it in the RingCentral admin portal, because it cannot be set per person from here." />
                  </Box>
                </Box>
                <Chip text="Managed in RingCentral" tone={{ bg: ACTIONS.DEFAULT, border: BORDER.DEFAULT, ink: GREY.SECONDARY }} />
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginTop: "14px",
                  paddingTop: "14px",
                  borderTop: `1px solid ${BORDER.LIGHT}`,
                }}
              >
                <Hint text="Provider" />
                <TypographyInline
                  size={"sm"}
                  color={GREY.SECONDARY}
                  fontWeight={fontWeight.REGULAR}
                  label="RingCentral"
                />
              </Box>
            </Box>

          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default CallingSetting;
